import React from "react";
import { Link, useLocation } from "react-router-dom";
import { createGuestAuthClient } from "../../data/auth";
import useSessionStore from "../../state/session";
import "./LoginPage.css";
type RecoveryCapability = {
  owner: string;
  capturedAt: number;
  token: string | null;
  access: string | null;
  refresh: string | null;
};
// A cold-boot principal cleanup can remount public routes. Keep only the
// unconsumed link in memory across that boundary, never in browser storage.
let pendingCapability: RecoveryCapability | null = null;
function captureCapability(owner: string) {
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const token =
    query.get("type") === "recovery" ? query.get("token_hash") : null;
  const access =
    hash.get("type") === "recovery" ? hash.get("access_token") : null;
  const refresh =
    hash.get("type") === "recovery" ? hash.get("refresh_token") : null;
  if (token || (access && refresh)) {
    pendingCapability = {
      owner,
      capturedAt: Date.now(),
      token,
      access,
      refresh,
    };
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname,
    );
  }
  if (
    pendingCapability?.owner !== owner ||
    Date.now() - pendingCapability.capturedAt > 60000
  )
    pendingCapability = null;
  return pendingCapability;
}
export default function ResetPasswordPage() {
  const location = useLocation();
  const authStatus = useSessionStore((s) => s.authStatus);
  const capability = React.useMemo(
    () => captureCapability(location.key),
    [location.key],
  );
  const client = React.useMemo(createGuestAuthClient, []);
  const [loading, setLoading] = React.useState(true);
  const [ready, setReady] = React.useState(false);
  const [factorId, setFactorId] = React.useState("");
  const [code, setCode] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [invalid, setInvalid] = React.useState("");
  const focusInvalid = (id: string) => {
    setInvalid(id);
    document.getElementById(id)?.focus();
  };
  const [done, setDone] = React.useState(false);
  const started = React.useRef(false);
  React.useEffect(
    () => () => {
      if (window.location.pathname !== "/reset-password")
        pendingCapability = null;
    },
    [],
  );
  React.useEffect(() => {
    if (authStatus === "checking") return;
    if (started.current) return;
    started.current = true;
    pendingCapability = null;
    async function recover() {
      try {
        if (capability?.token) {
          const { error } = await client.auth.verifyOtp({
            token_hash: capability.token,
            type: "recovery",
          });
          if (error) throw error;
        } else if (capability?.access && capability.refresh) {
          const { error } = await client.auth.setSession({
            access_token: capability.access,
            refresh_token: capability.refresh,
          });
          if (error) throw error;
        } else throw new Error("Missing recovery link");
        const { data, error } = await client.auth.mfa.listFactors();
        if (error) throw error;
        setFactorId(data.totp[0]?.id || "");
        setReady(true);
      } catch {
        setMessage(
          "This recovery link is invalid or expired. Request a new link from guest sign-in.",
        );
      } finally {
        setLoading(false);
      }
    }
    void recover();
  }, [client, authStatus, capability]);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setMessage("");
    setInvalid("");
    if (factorId && !/^\d{6}$/.test(code)) {
      setMessage("Enter the current six-digit authenticator code.");
      focusInvalid("recovery-code");
      return;
    }
    if (!factorId && (password.length < 15 || password !== confirm)) {
      setMessage(
        password.length < 15
          ? "Use at least 15 characters. A unique passphrase works well."
          : "Passwords do not match.",
      );
      focusInvalid(
        password.length < 15 ? "recovery-password" : "recovery-confirm",
      );
      return;
    }
    setBusy(true);
    try {
      if (factorId) {
        const { error } = await client.auth.mfa.challengeAndVerify({
          factorId,
          code,
        });
        if (error) throw error;
        setFactorId("");
        setCode("");
      } else {
        const { error } = await client.auth.updateUser({ password });
        if (error) throw error;
        await client.auth.signOut({ scope: "local" });
        setReady(false);
        setDone(true);
        setPassword("");
        setConfirm("");
        setMessage(
          "Your password has been updated. Sign in with your new password.",
        );
      }
    } catch {
      setMessage(
        factorId
          ? "That code could not be verified. Use the current code and try again."
          : "Your password could not be updated. Try a different unique passphrase, or request a new recovery link.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="portal-login" style={{ maxWidth: 540 }}>
      <header className="portal-login__intro">
        <h1>Reset your guest password</h1>
        <p>
          {loading
            ? "Checking your recovery link…"
            : factorId
              ? "Verify your authenticator before changing your password."
              : "Use a unique passphrase of at least 15 characters."}
        </p>
      </header>
      <section className="card pad-lg">
        {ready && (
          <form noValidate className="portal-login__form" onSubmit={submit}>
            <fieldset disabled={busy}>
              {factorId ? (
                <div className="portal-login__field">
                  <label htmlFor="recovery-code">Authenticator code</label>
                  <input
                    id="recovery-code"
                    aria-invalid={invalid === "recovery-code"}
                    aria-describedby="recovery-status"
                    className="input"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    autoFocus
                  />
                </div>
              ) : (
                <>
                  <div className="portal-login__field">
                    <label htmlFor="recovery-password">New password</label>
                    <input
                      id="recovery-password"
                      aria-invalid={invalid === "recovery-password"}
                      aria-describedby="recovery-status"
                      className="input"
                      type="password"
                      autoComplete="new-password"
                      minLength={15}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoFocus
                    />
                  </div>
                  <div className="portal-login__field">
                    <label htmlFor="recovery-confirm">
                      Confirm new password
                    </label>
                    <input
                      id="recovery-confirm"
                      aria-invalid={invalid === "recovery-confirm"}
                      aria-describedby="recovery-status"
                      className="input"
                      type="password"
                      autoComplete="new-password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                    />
                  </div>
                </>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                aria-busy={busy}
              >
                {busy
                  ? "Please wait…"
                  : factorId
                    ? "Verify authenticator"
                    : "Update password"}
              </button>
            </fieldset>
          </form>
        )}
        <p id="recovery-status" role="status" aria-live="polite">
          {message}
        </p>
        <Link className="btn" to="/login">
          {done ? "Sign in" : "Back to sign-in"}
        </Link>
      </section>
    </main>
  );
}
